import { displayTitle, type Memo, type MemoSummary, type MemoVisibility } from '@buakei/types';
import { Badge, Button, EmptyState, ErrorState, LoadingState, Notice } from '@buakei/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Download, FileText, Printer } from 'lucide-react';
import { Fragment, useId, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import type { IdentityClient } from '@/auth/identity-client';
import { ExternalSourceItem } from '@/assistant/ExternalSources';
import { ReportProblem } from '@/assistant/ReportProblem';
import { useAuth } from '@/auth/session';
import { useReaderTokens } from '@/pages/library/LibraryPages';
import { ApiError, memoApi } from '@/services/api';
import { saveFile, WORD_FILE } from '@/services/save-file';
import { Page } from '@/shell/Page';

/**
 * Research memos (M5, ADR-024): answers saved into the active Professional workspace, edited
 * by a lawyer with every version kept, and reviewed by a lawyer before anyone relies on them.
 */

export type MemoWorkspace = { id: string; name: string; kind: string };

/** The active workspace, when it is a Professional one where this account is a lawyer. */
export function useMemoWorkspace(): MemoWorkspace | undefined {
  const { session } = useAuth();
  const tokens = useReaderTokens();
  if (!tokens || !session?.activeWorkspaceId) return undefined;
  const workspace = session.workspaces.find((w) => w.id === session.activeWorkspaceId);
  const membership = session.memberships.find((m) => m.workspaceId === workspace?.id);
  if (!workspace || workspace.kind === 'learning' || !membership?.roles.includes('lawyer')) {
    return undefined;
  }
  return { id: workspace.id, name: workspace.name, kind: workspace.kind };
}

const keys = {
  list: (ws: string) => ['memos', ws] as const,
  memo: (ws: string, id: string) => ['memos', ws, id] as const,
};

function ReviewBadge({ memo }: { memo: MemoSummary }) {
  if (!memo.review) return <Badge>AI draft — not reviewed</Badge>;
  if (memo.review.version !== memo.currentVersion) {
    return <Badge>Changed since review</Badge>;
  }
  return <Badge tone="success">Reviewed by a lawyer</Badge>;
}

const VISIBILITY_LABEL: Record<MemoVisibility, string> = {
  private: 'Only the author',
  workspace: 'Shared with the workspace',
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export function MemosPage() {
  const tokens = useReaderTokens();
  const workspace = useMemoWorkspace();
  const memos = useQuery({
    queryKey: keys.list(workspace?.id ?? ''),
    queryFn: () => memoApi.list(tokens!, workspace!.id),
    enabled: Boolean(tokens && workspace),
  });
  return (
    <Page
      title="Research memos"
      description={
        workspace
          ? `Answers saved in ${workspace.name}. A memo is an AI draft until a lawyer reviews it.`
          : undefined
      }
    >
      {!workspace ? (
        <EmptyState title="Research memos need a lawyer role in a Professional workspace">
          Choose a Personal or Firm workspace where you are a lawyer, then save an answer from
          Research.
        </EmptyState>
      ) : null}
      {memos.isPending && workspace ? <LoadingState label="Loading memos" /> : null}
      {memos.isError ? (
        <ErrorState title="Memos could not be loaded" onRetry={() => void memos.refetch()}>
          {memos.error.message}
        </ErrorState>
      ) : null}
      {memos.data && memos.data.memos.length === 0 ? (
        <EmptyState title="No memos yet">
          In{' '}
          <Link to="/professional/research" className="underline">
            Research
          </Link>
          , ask a question, then choose "Save as memo" under the answer.
        </EmptyState>
      ) : null}
      {memos.data && memos.data.memos.length > 0 ? (
        <ul className="flex flex-col divide-y divide-line-200 rounded-lg border border-line-200 bg-white">
          {memos.data.memos.map((m) => (
            <li key={m.id} className="flex flex-col gap-1 p-4">
              <Link
                to={`/professional/memos/${encodeURIComponent(m.id)}`}
                className="font-medium text-navy-900 underline-offset-2 hover:underline"
              >
                {m.title}
              </Link>
              <span className="flex flex-wrap items-center gap-2 text-sm text-muted-600">
                <ReviewBadge memo={m} />
                <span>{VISIBILITY_LABEL[m.visibility]}</span>
                <span>· {m.authorName ?? 'Unknown author'}</span>
                <span>· Updated {formatDate(m.updatedAt)}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </Page>
  );
}

/** Memo text with its citation numbers ("[1,2]") linked to the sources below. */
function MemoBody({ body, anchor }: { body: string; anchor: (n: number) => string }) {
  const paragraphs = body.split(/\n+/).filter((p) => p.trim());
  const withLinks = (text: string): ReactNode[] =>
    text.split(/(\[\d+(?:,\s*\d+)*\])/).map((part, i) => {
      const numbers = /^\[(\d+(?:,\s*\d+)*)\]$/.exec(part)?.[1];
      if (!numbers) return <Fragment key={i}>{part}</Fragment>;
      return (
        <sup key={i} className="text-xs">
          {numbers.split(',').map((n, j) => (
            <Fragment key={n}>
              {j > 0 ? ',' : ''}
              <a href={`#${anchor(Number(n))}`} className="text-navy-900 underline">
                {n.trim()}
              </a>
            </Fragment>
          ))}
        </sup>
      );
    });
  return (
    <div className="flex flex-col gap-3 leading-relaxed text-ink-900">
      {paragraphs.map((p, i) => (
        <p key={i}>{withLinks(p)}</p>
      ))}
    </div>
  );
}

/**
 * Export one version (ADR-025): a Word file built by the server, or the browser's print dialog
 * (which can also save a PDF). Either way the server records the export first; an export of
 * an unreviewed version carries the AI-draft warning.
 */
function ExportControls({
  tokens,
  workspace,
  memo,
  version,
}: {
  tokens: IdentityClient;
  workspace: MemoWorkspace;
  memo: Memo;
  version: number;
}) {
  const word = useMutation({
    mutationFn: async () => {
      const file = await memoApi.exportDocx(tokens, workspace.id, memo.id, version);
      await saveFile(file, `${memo.title} (version ${version})`, WORD_FILE);
    },
  });
  const print = useMutation({
    mutationFn: () => memoApi.recordPrint(tokens, workspace.id, memo.id, version),
    onSuccess: () => window.print(),
  });
  const error = word.error ?? print.error;
  return (
    <section data-print="hide" aria-label="Export" className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" disabled={word.isPending} onClick={() => word.mutate()}>
          <Download aria-hidden /> {word.isPending ? 'Preparing…' : 'Download Word file'}
        </Button>
        <Button variant="secondary" disabled={print.isPending} onClick={() => print.mutate()}>
          <Printer aria-hidden /> Print or save as PDF
        </Button>
      </div>
      <p className="text-sm text-muted-600">
        Exports version {version}
        {memo.review?.version === version
          ? ', as reviewed.'
          : ', marked as an AI draft not reviewed by a lawyer.'}{' '}
        Each export is recorded.
      </p>
      {error ? (
        <p role="alert" className="text-sm text-ink-900">
          {error.message}
        </p>
      ) : null}
    </section>
  );
}

function MemoView({
  tokens,
  workspace,
  memo,
}: {
  tokens: IdentityClient;
  workspace: MemoWorkspace;
  memo: Memo;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const editId = useId();
  const shareId = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [shown, setShown] = useState(memo.currentVersion);
  const [confirming, setConfirming] = useState<'review' | 'delete' | null>(null);
  const current = memo.versions.find((v) => v.version === shown) ?? memo.versions.at(-1);
  const reviewedNow = memo.review?.version === memo.currentVersion;
  const anchor = (n: number) => `memo-source-${n}`;

  const updated = (next: Memo) => {
    queryClient.setQueryData(keys.memo(workspace.id, memo.id), next);
    void queryClient.invalidateQueries({ queryKey: keys.list(workspace.id), exact: true });
    setShown(next.currentVersion);
    setConfirming(null);
  };
  const edit = useMutation({
    mutationFn: () =>
      memoApi.edit(tokens, workspace.id, memo.id, {
        body: draft,
        expectedVersion: memo.currentVersion,
      }),
    onSuccess: (next) => {
      setEditing(false);
      updated(next);
    },
  });
  const review = useMutation({
    mutationFn: () => memoApi.review(tokens, workspace.id, memo.id, memo.currentVersion),
    onSuccess: updated,
  });
  const share = useMutation({
    mutationFn: (v: MemoVisibility) => memoApi.share(tokens, workspace.id, memo.id, v),
    onSuccess: updated,
  });
  const remove = useMutation({
    mutationFn: () => memoApi.remove(tokens, workspace.id, memo.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.list(workspace.id), exact: true });
      void navigate('/professional/memos');
    },
  });
  const failure = [edit, review, share, remove].find((m) => m.isError)?.error;
  const conflict = failure instanceof ApiError && failure.status === 409;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-600">
        <ReviewBadge memo={memo} />
        <span>{VISIBILITY_LABEL[memo.visibility]}</span>
        <span>· By {memo.authorName ?? 'Unknown author'}</span>
        <span>· Version {memo.currentVersion}</span>
      </div>
      {!reviewedNow ? (
        <Notice title="AI draft — not reviewed by a lawyer" icon={AlertTriangle}>
          {memo.review
            ? `Version ${memo.review.version} was reviewed by ${memo.review.reviewerName ?? 'a lawyer'}; this version has changed since.`
            : 'A lawyer must review this memo before anyone relies on it or it is sent outside the workspace.'}
        </Notice>
      ) : (
        <p className="text-sm text-ink-900">
          Version {memo.review!.version} reviewed by {memo.review!.reviewerName ?? 'a lawyer'} on{' '}
          {formatDate(memo.review!.reviewedAt)}.
        </p>
      )}
      <section aria-label="Question" className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold text-navy-900">Question asked</h2>
        <p className="text-ink-900">{memo.question}</p>
      </section>

      {editing ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            edit.mutate();
          }}
        >
          <label htmlFor={editId} className="text-sm font-semibold text-navy-900">
            Memo text
          </label>
          <textarea
            id={editId}
            rows={16}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="rounded-md border border-line-200 bg-white p-3 leading-relaxed text-ink-900"
          />
          <p className="text-sm text-muted-600">
            Keep citation numbers such as [1] next to the statements they support. Saving keeps the
            earlier versions.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={edit.isPending || !draft.trim()}>
              {edit.isPending ? 'Saving…' : 'Save as a new version'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <section aria-label="Memo" className="flex flex-col gap-3">
          {current && current.version !== memo.currentVersion ? (
            <p className="text-sm text-ink-900">
              Showing version {current.version}, an earlier version.{' '}
              <button
                type="button"
                className="font-medium text-navy-900 underline"
                onClick={() => setShown(memo.currentVersion)}
              >
                Show the current version
              </button>
            </p>
          ) : null}
          {current ? <MemoBody body={current.body} anchor={anchor} /> : null}
        </section>
      )}

      {!editing ? (
        <div data-print="hide" className="flex flex-wrap items-center gap-2">
          {memo.canEdit ? (
            <Button
              variant="secondary"
              onClick={() => {
                setDraft(memo.versions.at(-1)?.body ?? '');
                setEditing(true);
              }}
            >
              Edit
            </Button>
          ) : null}
          {!reviewedNow ? (
            confirming === 'review' ? (
              <>
                <Button disabled={review.isPending} onClick={() => review.mutate()}>
                  Confirm: I reviewed version {memo.currentVersion}
                </Button>
                <Button variant="secondary" onClick={() => setConfirming(null)}>
                  Not yet
                </Button>
              </>
            ) : (
              <Button onClick={() => setConfirming('review')}>Mark as reviewed by me</Button>
            )
          ) : null}
          {memo.canEdit ? (
            confirming === 'delete' ? (
              <>
                <Button
                  variant="secondary"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  Confirm delete
                </Button>
                <Button variant="secondary" onClick={() => setConfirming(null)}>
                  Keep the memo
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={() => setConfirming('delete')}>
                Delete
              </Button>
            )
          ) : null}
        </div>
      ) : null}
      {!editing && current ? (
        <ExportControls
          tokens={tokens}
          workspace={workspace}
          memo={memo}
          version={current.version}
        />
      ) : null}
      {failure ? (
        <div data-print="hide" role="alert" className="flex flex-col gap-2 text-sm text-ink-900">
          <p>{failure.message}</p>
          {conflict ? (
            <Button
              size="sm"
              variant="secondary"
              className="self-start"
              onClick={() => {
                [edit, review, share].forEach((m) => m.reset());
                void queryClient.invalidateQueries({ queryKey: keys.memo(workspace.id, memo.id) });
              }}
            >
              Reload the memo
            </Button>
          ) : null}
        </div>
      ) : null}

      {memo.canEdit && workspace.kind === 'firm' ? (
        <div data-print="hide" className="flex flex-col gap-1">
          <label htmlFor={shareId} className="text-sm font-medium text-ink-900">
            Who can read it
          </label>
          <select
            id={shareId}
            value={memo.visibility}
            disabled={share.isPending}
            onChange={(e) => share.mutate(e.target.value as MemoVisibility)}
            className="h-10 max-w-sm rounded-md border border-line-200 bg-white px-2 text-ink-900"
          >
            <option value="private">Only me</option>
            <option value="workspace">Lawyers in {workspace.name}</option>
          </select>
        </div>
      ) : null}

      <section aria-label="Sources" className="flex flex-col gap-3 border-t border-line-200 pt-4">
        <h2 className="text-sm font-semibold text-navy-900">Sources</h2>
        <ol className="flex flex-col gap-3">
          {memo.sources.map((s) =>
            s.collection === 'external' ? (
              <ExternalSourceItem key={s.n} s={s} id={anchor(s.n)} />
            ) : (
              <li key={s.n} id={anchor(s.n)} className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-navy-900">
                  [{s.n}] {displayTitle(s.title)} — {s.label}
                </span>
                {s.quotes.map((q) => (
                  <blockquote key={q} className="border-l-2 border-line-200 pl-3 text-ink-900">
                    “{q}”
                  </blockquote>
                ))}
                <Link
                  to={`/library/${s.collection === 'cases' ? 'cases/' : ''}${encodeURIComponent(s.versionId)}?page=${s.pageStart}`}
                  state={{ backTo: `${location.pathname}${location.search}` }}
                  className="flex items-center gap-1 self-start font-medium text-navy-900 underline"
                >
                  <FileText className="size-4" aria-hidden /> Open the original at page{' '}
                  {s.pageStart}
                </Link>
                <div data-print="hide">
                  <ReportProblem
                    versionId={s.versionId}
                    passageId={s.passageId}
                    context="memo"
                    label="Report a problem with this source"
                  />
                </div>
              </li>
            ),
          )}
        </ol>
        <p className="text-xs text-muted-600">
          Sources were saved with the answer. Text added in editing has no source of its own unless
          it cites one of these. Legal information, not legal advice.
        </p>
      </section>

      <section
        data-print="hide"
        aria-label="Versions"
        className="flex flex-col gap-2 border-t border-line-200 pt-4"
      >
        <h2 className="text-sm font-semibold text-navy-900">Versions</h2>
        <ol className="flex flex-col gap-1 text-sm">
          {[...memo.versions].reverse().map((v) => (
            <li key={v.version} className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                aria-pressed={shown === v.version}
                onClick={() => setShown(v.version)}
                className="font-medium text-navy-900 underline"
              >
                Version {v.version}
              </button>
              <span className="text-muted-600">
                {v.kind === 'ai_draft' ? 'AI draft' : `Edited by ${v.authorName ?? 'the author'}`} ·{' '}
                {formatDate(v.createdAt)}
                {memo.review?.version === v.version ? ' · reviewed' : ''}
              </span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

export function MemoPage() {
  const tokens = useReaderTokens();
  const workspace = useMemoWorkspace();
  const { memoId = '' } = useParams();
  const memo = useQuery({
    queryKey: keys.memo(workspace?.id ?? '', memoId),
    queryFn: () => memoApi.get(tokens!, workspace!.id, memoId),
    enabled: Boolean(tokens && workspace),
  });
  return (
    <Page title={memo.data?.title ?? 'Research memo'}>
      <Link
        data-print="hide"
        to="/professional/memos"
        className="flex items-center gap-1 self-start text-sm font-medium text-navy-900 underline"
      >
        <ArrowLeft className="size-4" aria-hidden /> All memos
      </Link>
      {!workspace ? (
        <EmptyState title="Research memos need a lawyer role in a Professional workspace" />
      ) : null}
      {memo.isPending && workspace ? <LoadingState label="Loading the memo" /> : null}
      {memo.isError ? (
        <ErrorState title="This memo could not be opened" onRetry={() => void memo.refetch()}>
          {memo.error instanceof ApiError && memo.error.status === 404
            ? 'It is not in this workspace, or it is not shared with you.'
            : memo.error.message}
        </ErrorState>
      ) : null}
      {memo.data && tokens && workspace ? (
        <MemoView tokens={tokens} workspace={workspace} memo={memo.data} />
      ) : null}
    </Page>
  );
}
